/**
 * @module services/api-upload/applyCustomizeSettingPatches
 * @description upload 前补丁分发器：按 options.patches 调用内置修正逻辑
 *
 * 调用方只需在 buildCustomizeSettingForApi 传 patches 配置，无需单独 import 各 patch 实现。
 * convert 产出的是 Context base/draft 的「持久化快照」，部分 QB 瞬时状态（如对比时间开关）
 * 不会自动反映其中，故在 omit 空字段之前集中做按需补丁。
 */

import { patchColumnSortCompareTimeInCustomizeSetting } from './patchColumnSortCompareTime';
import type {
  IBuildCustomizeSettingOptions,
  IPrepareConfigInput,
} from './types';

/**
 * 对 convert 后的 customizeSetting 按 patches 配置依次应用内置补丁。
 *
 * @param customizeSetting - convert 返回值（可能为 undefined）
 * @param input - 归一化后的 build 入参（含 chartType、columnDimensions 等）
 * @param options - 补丁开关；未传则原样返回
 * @returns 补丁后的 customizeSetting；各 patch 无实质变更时尽量保持引用
 */
export function applyCustomizeSettingPatches(
  customizeSetting: Record<string, unknown> | undefined,
  input: IPrepareConfigInput,
  options?: IBuildCustomizeSettingOptions,
): Record<string, unknown> | undefined {
  const patches = options?.patches;
  // 未声明任何补丁：与历史行为一致，避免影响 Search / 保存等默认路径
  if (!patches) {
    return customizeSetting;
  }

  let result = customizeSetting;

  // ----- 补丁：对比时间 ↔ column_sort 展示名 -----
  // base 可能在开启对比时间时被持久化为「对比时间」展示名；关闭后需按最新 filters 还原
  if (patches.columnSortCompareTime) {
    result = patchColumnSortCompareTimeInCustomizeSetting({
      customizeSetting: result,
      chartType: input.chartType,
      columnFilters: options.columnFilters,
      columnDimensions: input.columnDimensions,
    });
  }

  // TODO(api-upload): 新增补丁类型时在此追加 if 分支，并同步扩展 IBuildCustomizeSettingPatches

  return result;
}
